import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Plus, Trash, DownloadSimple, UploadSimple, FileXls, FilePdf,
  Database, MagnifyingGlass, FloppyDisk, Check, X, Lock, LockOpen, PencilSimple,
  Eye, EyeSlash, Clock, ShieldWarning, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ModuleGuide } from '../components/GuidanceComponents';
import { LineItemTypeahead } from '../components/LineItemTypeahead';
import { MarginHealthMeter, MarginBadge } from '../components/MarginHealth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BOQEditor = () => {
  const navigate = useNavigate();
  const { projectId, quoteId } = useParams();
  const { user } = useAuth();
  const isNew = quoteId === 'new';
  const fileInputRef = useRef(null);
  const smartImportRef = useRef(null);

  const [project, setProject] = useState(null);
  const [trades, setTrades] = useState([]);
  const [title, setTitle] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [taxType, setTaxType] = useState('IGST');
  const [assumptions, setAssumptions] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [marginHealth, setMarginHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quoteLocked, setQuoteLocked] = useState(false);
  const [quoteRef, setQuoteRef] = useState('');
  const [variationOrders, setVariationOrders] = useState([]);
  const [showVOForm, setShowVOForm] = useState(false);
  const [voForm, setVoForm] = useState({ description: '', specification: '', make: '', unit: 'Nos', quantity: 0, rate: 0, trade_id: '', reason: '' });
  const [includeAssumptions, setIncludeAssumptions] = useState(false);

  // Line Item Master
  const [masterDialogOpen, setMasterDialogOpen] = useState(false);
  const [masterItems, setMasterItems] = useState([]);
  const [masterSearch, setMasterSearch] = useState('');
  const [masterFilterTrade, setMasterFilterTrade] = useState('');
  const [smartImportOpen, setSmartImportOpen] = useState(false);
  const [importResults, setImportResults] = useState([]);
  const [importUploading, setImportUploading] = useState(false);

  // RBAC: Financial Access
  const [financialAccess, setFinancialAccess] = useState({ has_financial_access: true, can_request_access: false, has_pending_request: false, is_financially_restricted: false });
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestDuration, setRequestDuration] = useState(7);
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  // BOQ Change Control
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changeTarget, setChangeTarget] = useState(null); // {index, field, label, oldValue}
  const [changeNewValue, setChangeNewValue] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);

  // Suggest to Master dialog
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);
  const [suggestForm, setSuggestForm] = useState({
    description: '', unit: 'Sqft', trade_id: '', remarks: '', urgency: 'medium',
  });
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const canDirectEdit = isSuperAdmin;
  const isViewer = user?.role === 'viewer';
  const isReadOnly = isViewer || quoteLocked;
  const canSeeFinancials = !financialAccess.is_financially_restricted;

  useEffect(() => { fetchData(); }, [projectId, quoteId]);

  const fetchData = async () => {
    try {
      const calls = [
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/financial-access/check/${projectId}`, { withCredentials: true }),
      ];
      const [projectRes, tradesRes, finAccessRes] = await Promise.all(calls);
      setProject(projectRes.data);
      setTrades(tradesRes.data);
      setFinancialAccess(finAccessRes.data);

      if (!isNew) {
        const quoteRes = await axios.get(`${BACKEND_URL}/api/quotes/enhanced/${quoteId}`, { withCredentials: true });
        const quote = quoteRes.data;
        setTitle(quote.title);
        setPaymentTerms(quote.payment_terms);
        setTaxType(quote.tax_type);
        setAssumptions(quote.assumptions || '');
        setQuoteLocked(quote.is_locked || false);
        setQuoteRef(quote.quote_ref || '');
        setLineItems(quote.line_items.map((item) => ({
          trade_id: item.trade_id, description: item.description,
          specification: item.specification || '', make: item.make || '',
          unit: item.unit, quantity: item.quantity, rate: item.rate,
          subcontractor_rate: item.subcontractor_rate || 0, remarks: item.remarks || '',
          material_cost: item.material_cost, labour_cost: item.labour_cost,
          margin_cost: item.margin_cost, total_amount: item.total_amount,
          trade_name: item.trade_name || '', trade_type: item.trade_type || 'in_house',
          hsn_code: item.hsn_code || '', material_id: item.material_id || '',
          material_recipe: item.material_recipe || null,
          manpower_recipe: item.manpower_recipe || null,
          target_cost_per_unit: item.target_cost_per_unit || 0,
          reference_image: item.reference_image || '',
          // Phase 1
          execution_type: item.execution_type || '',
          basic_rate_breakup: item.basic_rate_breakup || '',
          inclusions: item.inclusions || [],
          exclusions: item.exclusions || [],
          preferred_subcontractor_id: item.preferred_subcontractor_id || '',
          master_item_id: item.master_item_id || null,
          item_code: item.item_code || '',
          source_type: item.source_type || '',
        })));
        if (quote.is_locked) {
          try { const voRes = await axios.get(`${BACKEND_URL}/api/quotes/${quoteId}/variation-orders`, { withCredentials: true }); setVariationOrders(voRes.data); } catch {}
        }
        // Load pending BOQ changes for this quote
        try {
          const changesRes = await axios.get(`${BACKEND_URL}/api/boq-changes?project_id=${projectId}&status=pending`, { withCredentials: true });
          setPendingChanges(changesRes.data);
        } catch {}
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const getTradeById = (tradeId) => trades.find((t) => t.id === tradeId);

  const computeLocalCosts = (item) => {
    const trade = getTradeById(item.trade_id);
    if (!trade) return { material_cost: 0, labour_cost: 0, margin_cost: 0, total_amount: 0 };
    const rate = parseFloat(item.rate) || 0;
    const qty = parseFloat(item.quantity) || 0;
    const subRate = parseFloat(item.subcontractor_rate) || 0;
    const tradeType = trade.trade_type || 'in_house';
    let material_cost = 0, labour_cost = 0, margin_cost = 0;
    if (tradeType === 'in_house') {
      material_cost = rate * (trade.material_percentage / 100) * qty;
      labour_cost = rate * (trade.labour_percentage / 100) * qty;
      margin_cost = rate * (trade.margin_percentage / 100) * qty;
    } else if (tradeType === 'subcontract_with_material') {
      material_cost = 0; labour_cost = subRate * qty; margin_cost = (rate * qty) - (subRate * qty);
    } else if (tradeType === 'subcontract_without_material') {
      material_cost = rate * (trade.material_percentage / 100) * qty;
      labour_cost = subRate * qty; margin_cost = rate * (trade.margin_percentage / 100) * qty;
    }
    return {
      material_cost: Math.round(material_cost * 100) / 100,
      labour_cost: Math.round(labour_cost * 100) / 100,
      margin_cost: Math.round(margin_cost * 100) / 100,
      total_amount: Math.round(rate * qty * 100) / 100,
    };
  };

  const addLineItem = () => {
    const defaultTrade = trades[0];
    setLineItems([...lineItems, {
      trade_id: defaultTrade?.id || '', description: '', specification: '', make: '',
      unit: 'Sqft', quantity: 0, rate: 0, subcontractor_rate: 0, remarks: '', master_item_id: null,
      hsn_code: defaultTrade?.default_hsn_code || '', material_id: '', reference_image: '',
    }]);
  };

  const openMasterPicker = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/line-item-master`, { withCredentials: true });
      setMasterItems(res.data); setMasterSearch(''); setMasterFilterTrade(''); setMasterDialogOpen(true);
    } catch { toast.error('Failed to load master items'); }
  };

  const addFromMaster = (masterItem) => {
    const trade = trades.find(t => t.id === masterItem.trade_id);
    // Build material_recipe from master materials
    const materialRecipe = (masterItem.materials || []).map(m => ({
      material_name: m.material_name || '', material_id: m.material_id || '',
      consumption_per_unit: m.quantity_per_unit || 0, unit: m.unit || '',
      wastage_percentage: m.wastage_percentage || 0, remarks: m.remarks || '',
    }));
    // Build manpower_recipe from master manpower_entries
    const manpowerRecipe = (masterItem.manpower_entries || []).map(mp => ({
      labour_type: mp.labour_type || 'Skilled', role_name: mp.role_name || '',
      manpower_per_unit: mp.manpower_per_unit || 0, remarks: mp.remarks || '',
    }));
    setLineItems([...lineItems, {
      trade_id: masterItem.trade_id, description: masterItem.description,
      specification: masterItem.specification || '', make: masterItem.make || '',
      unit: masterItem.unit, quantity: 0, rate: masterItem.last_used_rate || masterItem.base_rate,
      subcontractor_rate: 0, remarks: '', master_item_id: masterItem.id,
      item_code: masterItem.item_code || '', source_type: 'master',
      hsn_code: trade?.default_hsn_code || '', material_id: '',
      material_recipe: materialRecipe.length > 0 ? materialRecipe : null,
      manpower_recipe: manpowerRecipe.length > 0 ? manpowerRecipe : null,
      // Phase 1 — Execution & client-facing
      execution_type: masterItem.execution_type || 'in_house',
      basic_rate_breakup: masterItem.basic_rate_breakup || '',
      inclusions: masterItem.inclusions || [],
      exclusions: masterItem.exclusions || [],
      preferred_subcontractor_id: masterItem.preferred_subcontractor_id || '',
    }]);
    axios.post(`${BACKEND_URL}/api/line-item-master/increment-usage/${masterItem.id}`, { rate: masterItem.base_rate }, { withCredentials: true }).catch(() => {});
    toast.success(`Added: ${masterItem.description}`);
  };

  const openSuggestDialog = (item) => {
    setSuggestForm({
      description: item.description || '',
      unit: item.unit || 'Sqft',
      trade_id: item.trade_id || '',
      remarks: item.specification || '',
      urgency: 'medium',
    });
    setSuggestDialogOpen(true);
  };

  const submitSuggestion = async () => {
    if (!suggestForm.description.trim()) { toast.error('Description required'); return; }
    setSuggestSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/item-suggestions`, {
        description: suggestForm.description.trim(),
        unit: suggestForm.unit || 'Sqft',
        trade_id: suggestForm.trade_id || '',
        remarks: suggestForm.remarks || '',
        urgency: suggestForm.urgency || 'medium',
        source_project_id: projectId,
        source_quote_id: isNew ? '' : quoteId,
      }, { withCredentials: true });
      toast.success('Item suggested — Admin will review and add it to Master Studio');
      setSuggestDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setSuggestSubmitting(false);
    }
  };

  const handleSmartImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImportUploading(true); setImportResults([]);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/excel/smart-import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResults(res.data.results.map(r => ({ ...r, action: r.status === 'matched' ? 'accept' : 'new' })));
      setSmartImportOpen(true);
      toast.success(`Parsed ${res.data.total} items: ${res.data.matched} matched, ${res.data.new} new`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImportUploading(false); if (smartImportRef.current) smartImportRef.current.value = ''; }
  };

  const applySmartImport = () => {
    const newItems = importResults.map(r => {
      if (r.action === 'accept' && r.match) {
        const matRecipe = (r.match.materials || []).map(m => ({
          material_name: m.material_name || '', material_id: m.material_id || '',
          consumption_per_unit: m.quantity_per_unit || 0, unit: m.unit || '',
          wastage_percentage: m.wastage_percentage || 0, remarks: m.remarks || '',
        }));
        const mpRecipe = (r.match.manpower_entries || []).map(mp => ({
          labour_type: mp.labour_type || 'Skilled', role_name: mp.role_name || '',
          manpower_per_unit: mp.manpower_per_unit || 0, remarks: mp.remarks || '',
        }));
        return { trade_id: r.match.trade_id, description: r.match.description, specification: r.match.specification || '', make: r.match.make || '', unit: r.match.unit || r.input.unit || 'Nos', quantity: parseFloat(r.input.quantity) || 0, rate: parseFloat(r.input.rate) || r.match.base_rate, subcontractor_rate: 0, remarks: '', master_item_id: r.match.id, material_recipe: matRecipe.length > 0 ? matRecipe : null, manpower_recipe: mpRecipe.length > 0 ? mpRecipe : null };
      }
      const defaultTrade = trades[0];
      return { trade_id: defaultTrade?.id || '', description: r.input.description, specification: r.input.specification || '', make: r.input.make || '', unit: r.input.unit || 'Nos', quantity: parseFloat(r.input.quantity) || 0, rate: parseFloat(r.input.rate) || 0, subcontractor_rate: 0, remarks: '', master_item_id: null };
    });
    setLineItems([...lineItems, ...newItems]); setSmartImportOpen(false); setImportResults([]);
    toast.success(`Added ${newItems.length} items`);
  };

  const filteredMasterItems = masterItems.filter(i => {
    if (masterFilterTrade && i.trade_id !== masterFilterTrade) return false;
    if (!masterSearch) return true;
    return i.description.toLowerCase().includes(masterSearch.toLowerCase()) || i.item_code.toLowerCase().includes(masterSearch.toLowerCase());
  });

  const removeLineItem = (index) => setLineItems(lineItems.filter((_, i) => i !== index));

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-fetch HSN from trade when trade changes (if no custom HSN set)
      if (field === 'trade_id') {
        const trade = trades.find(t => t.id === value);
        if (trade?.default_hsn_code && !updated[index].hsn_code) {
          updated[index].hsn_code = trade.default_hsn_code;
        }
      }
      return updated;
    });
  };

  // BOQ Change Control: instead of direct edit, open change proposal dialog
  const handleFieldEdit = (index, field, label, oldValue) => {
    if (canDirectEdit) return; // super_admin can edit directly
    setChangeTarget({ index, field, label, oldValue: String(oldValue) });
    setChangeNewValue('');
    setChangeReason('');
    setChangeDialogOpen(true);
  };

  const submitChangeRequest = async () => {
    if (!changeReason.trim()) { toast.error('Reason is required'); return; }
    if (!changeNewValue.trim()) { toast.error('New value is required'); return; }
    setChangeSubmitting(true);
    try {
      const item = lineItems[changeTarget.index];
      await axios.post(`${BACKEND_URL}/api/boq-changes`, {
        project_id: projectId, quote_id: quoteId,
        line_item_id: `${changeTarget.index}`, field: `${changeTarget.label} (${item.description || 'Item #' + (changeTarget.index + 1)})`,
        old_value: changeTarget.oldValue, new_value: changeNewValue, reason: changeReason,
      }, { withCredentials: true });
      toast.success('Change request submitted for approval');
      setChangeDialogOpen(false);
      // Refresh pending changes
      const changesRes = await axios.get(`${BACKEND_URL}/api/boq-changes?project_id=${projectId}&status=pending`, { withCredentials: true });
      setPendingChanges(changesRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit change'); }
    finally { setChangeSubmitting(false); }
  };

  // Financial access request
  const handleRequestFinancialAccess = async () => {
    if (!requestReason.trim()) { toast.error('Reason is required'); return; }
    setRequestSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/financial-access/request`, {
        project_id: projectId, reason: requestReason, duration_days: requestDuration,
      }, { withCredentials: true });
      toast.success('Financial access request submitted');
      setRequestDialogOpen(false); setRequestReason(''); setRequestDuration(7);
      setFinancialAccess(prev => ({ ...prev, has_pending_request: true }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setRequestSubmitting(false); }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    lineItems.forEach((item) => { subtotal += computeLocalCosts(item).total_amount; });
    const taxAmount = subtotal * 0.18;
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Enter a quote title'); return; }
    if (!paymentTerms.trim()) { toast.error('Enter payment terms'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = {
        project_id: projectId, title, payment_terms: paymentTerms, tax_type: taxType, assumptions,
        line_items: lineItems.map((item) => ({
          trade_id: item.trade_id, description: item.description, specification: item.specification || '',
          make: item.make || '', unit: item.unit, quantity: parseFloat(item.quantity) || 0,
          rate: parseFloat(item.rate) || 0, subcontractor_rate: parseFloat(item.subcontractor_rate) || 0, remarks: item.remarks || '',
          hsn_code: item.hsn_code || '', material_id: item.material_id || '',
          material_recipe: item.material_recipe || null,
          manpower_recipe: item.manpower_recipe || null,
          reference_image: item.reference_image || '',
          // Phase 1 — Execution & client-facing
          execution_type: item.execution_type || '',
          basic_rate_breakup: item.basic_rate_breakup || '',
          inclusions: item.inclusions || [],
          exclusions: item.exclusions || [],
          preferred_subcontractor_id: item.preferred_subcontractor_id || '',
          master_item_id: item.master_item_id || '',
          item_code: item.item_code || '',
          source_type: item.source_type || '',
          target_cost_per_unit: parseFloat(item.target_cost_per_unit) || 0,
        })),
      };
      if (isNew) { await axios.post(`${BACKEND_URL}/api/quotes/enhanced`, payload, { withCredentials: true }); toast.success('Quote created'); }
      else { await axios.put(`${BACKEND_URL}/api/quotes/enhanced/${quoteId}`, payload, { withCredentials: true }); toast.success('Quote updated'); }
      navigate(`/projects/${projectId}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleExport = async (format) => {
    if (isNew) { toast.error('Save the quote before exporting'); return; }
    try {
      const params = includeAssumptions ? '?include_assumptions=true' : '';
      const response = await axios.get(`${BACKEND_URL}/api/quotes/${quoteId}/export/${format}${params}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `BOQ_${project?.pi_number || 'export'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error(`Failed to export ${format.toUpperCase()}`); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/excel/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'line_items_template.xlsx');
      document.body.appendChild(link); link.click(); link.remove(); toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/excel/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const imported = response.data.line_items || [];
      setLineItems([...lineItems, ...imported]); toast.success(`Imported ${imported.length} items`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const handleAddVO = async () => {
    if (!voForm.description || !voForm.trade_id || !voForm.reason) { toast.error('Fill required VO fields'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${quoteId}/variation-orders`, { ...voForm, quantity: parseFloat(voForm.quantity) || 0, rate: parseFloat(voForm.rate) || 0 }, { withCredentials: true });
      toast.success('Variation Order added'); setShowVOForm(false);
      setVoForm({ description: '', specification: '', make: '', unit: 'Nos', quantity: 0, rate: 0, trade_id: '', reason: '' });
      const voRes = await axios.get(`${BACKEND_URL}/api/quotes/${quoteId}/variation-orders`, { withCredentials: true });
      setVariationOrders(voRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteVO = async (voId) => {
    if (!window.confirm('Delete this variation order?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/variation-orders/${voId}`, { withCredentials: true });
      toast.success('VO deleted');
      const voRes = await axios.get(`${BACKEND_URL}/api/quotes/${quoteId}/variation-orders`, { withCredentials: true });
      setVariationOrders(voRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const isSubcontractTrade = (tradeId) => {
    const trade = getTradeById(tradeId);
    return trade && (trade.trade_type === 'subcontract_with_material' || trade.trade_type === 'subcontract_without_material');
  };

  const fmtCurrency = (v) => v.toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
  const RESTRICTED = '***';

  const { subtotal, taxAmount, total } = calculateTotals();

  const handleExportRiskSummary = async () => {
    if (isNew || !quoteId) { toast.error('Save the quote first to export'); return; }
    try {
      const payload = {
        line_items: lineItems.map(it => ({
          description: it.description,
          rate: parseFloat(it.rate) || 0,
          quantity: parseFloat(it.quantity) || 0,
          execution_type: it.execution_type || 'in_house',
          subcontractor_rate: parseFloat(it.subcontractor_rate) || 0,
          target_cost_per_unit: parseFloat(it.target_cost_per_unit) || 0,
          material_recipe: it.material_recipe || [],
          manpower_recipe: it.manpower_recipe || [],
        })),
      };
      const res = await axios.post(`${BACKEND_URL}/api/quotes/${quoteId}/risk-summary/pdf`, payload, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Risk_Summary_${quoteRef || 'BOQ'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Risk Summary exported');
    } catch { toast.error('Failed to export risk summary'); }
  };

  // Real-time margin health (debounced) — advisory only
  useEffect(() => {
    if (financialAccess.is_financially_restricted) { setMarginHealth(null); return; }
    if (!lineItems || lineItems.length === 0) { setMarginHealth(null); return; }
    const handle = setTimeout(async () => {
      try {
        const payload = {
          line_items: lineItems.map(it => ({
            description: it.description,
            rate: parseFloat(it.rate) || 0,
            quantity: parseFloat(it.quantity) || 0,
            execution_type: it.execution_type || 'in_house',
            subcontractor_rate: parseFloat(it.subcontractor_rate) || 0,
            target_cost_per_unit: parseFloat(it.target_cost_per_unit) || 0,
            material_recipe: it.material_recipe || [],
            manpower_recipe: it.manpower_recipe || [],
          })),
        };
        const { data } = await axios.post(`${BACKEND_URL}/api/margin-health`, payload, { withCredentials: true });
        setMarginHealth(data);
      } catch { /* advisory — ignore failures */ }
    }, 700);
    return () => clearTimeout(handle);
  }, [lineItems, financialAccess.is_financially_restricted]);


  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-start">
            <div>
              <Button onClick={() => navigate(`/projects/${projectId}`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
                <ArrowLeft size={20} className="mr-2" /> Back
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                {isNew ? 'New Quote' : quoteLocked ? 'View Quote' : 'Edit Quote'}
              </h1>
              <p className="text-sm text-zinc-600 mt-1">
                {project?.pi_number} - {project?.client_name}
                {quoteRef && <span className="ml-2 font-mono text-blue-700">{quoteRef}</span>}
              </p>
              {quoteLocked && <span className="inline-flex items-center mt-1 px-2 py-0.5 text-xs font-bold bg-green-100 text-green-800 border border-green-200"><Lock size={12} className="mr-1" /> Locked</span>}
              {!canDirectEdit && !isNew && !isViewer && !quoteLocked && (
                <span className="inline-flex items-center mt-1 ml-2 px-2 py-0.5 text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                  <ShieldWarning size={12} className="mr-1" /> Change Control Active
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap justify-end items-center">
              {!isNew && (
                <>
                  <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none" data-testid="include-assumptions-toggle">
                    <input type="checkbox" checked={includeAssumptions} onChange={(e) => setIncludeAssumptions(e.target.checked)} className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5" />
                    Include Assumptions
                  </label>
                  <Button onClick={() => handleExport('pdf')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100" data-testid="export-pdf-button"><FilePdf size={20} className="mr-2" /> PDF</Button>
                  <Button onClick={() => handleExport('excel')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100" data-testid="export-excel-button"><FileXls size={20} className="mr-2" /> Excel</Button>
                </>
              )}
              {canDirectEdit && !isReadOnly && (
                <Button onClick={handleSave} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="save-quote-button">
                  {saving ? 'Saving...' : 'Save Quote'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Create and manage project costing here. Enter line items with rates — the system auto-splits into material, labour, and margin based on trade settings." />

        {/* Financial Access Restriction Banner */}
        {financialAccess.is_financially_restricted && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex items-center justify-between" data-testid="financial-restriction-banner">
            <div className="flex items-center gap-3">
              <EyeSlash size={20} className="text-amber-700" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Financial data is restricted for your role</p>
                <p className="text-xs text-amber-600">Rates, costs, totals, and margin breakdowns are hidden. {financialAccess.can_request_access ? 'You can request temporary access.' : ''}</p>
              </div>
            </div>
            {financialAccess.can_request_access && !financialAccess.has_pending_request && (
              <Button onClick={() => setRequestDialogOpen(true)} variant="outline" className="border-amber-300 text-amber-700 rounded-none hover:bg-amber-100 text-xs" data-testid="request-financial-access-btn">
                <LockOpen size={14} className="mr-1" /> Request Financial Access
              </Button>
            )}
            {financialAccess.has_pending_request && (
              <span className="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1.5 border border-amber-300" data-testid="pending-access-badge">
                <Clock size={12} className="inline mr-1 -mt-0.5" /> Request Pending
              </span>
            )}
          </div>
        )}

        {/* Pending BOQ Changes Banner */}
        {pendingChanges.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 p-3 mb-6" data-testid="pending-changes-banner">
            <p className="text-xs font-semibold text-blue-800">
              <ShieldWarning size={14} className="inline mr-1 -mt-0.5" />
              {pendingChanges.length} pending change request{pendingChanges.length > 1 ? 's' : ''} awaiting approval
            </p>
            <div className="mt-2 space-y-1">
              {pendingChanges.slice(0, 3).map(c => (
                <div key={c.id} className="text-[10px] text-blue-700 flex items-center gap-2">
                  <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 font-bold">PENDING</span>
                  <span className="font-medium">{c.field}:</span>
                  <span className="line-through text-red-600">{c.old_value}</span>
                  <span>-&gt;</span>
                  <span className="text-green-700 font-semibold">{c.new_value}</span>
                </div>
              ))}
              {pendingChanges.length > 3 && <p className="text-[10px] text-blue-500">+{pendingChanges.length - 3} more...</p>}
            </div>
          </div>
        )}

        {/* Quote Details */}
        <div className="bg-white border border-zinc-200 p-6 mb-6">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-800 mb-4">Quote Details</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="title" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Quote Title *</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canDirectEdit || isReadOnly}
                className="mt-2 bg-zinc-50 border-zinc-200 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-none p-3" data-testid="quote-title-input" />
            </div>
            <div>
              <Label htmlFor="tax_type" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Tax Type *</Label>
              <select id="tax_type" value={taxType} onChange={(e) => setTaxType(e.target.value)} disabled={!canDirectEdit || isReadOnly}
                className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base" data-testid="tax-type-select">
                <option value="IGST">IGST @ 18%</option>
                <option value="CGST_SGST">CGST & SGST @ 9% each</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="payment_terms" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Payment Terms *</Label>
              <textarea id="payment_terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} disabled={!canDirectEdit || isReadOnly} rows={2}
                className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base" data-testid="payment-terms-input" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="assumptions" className="text-xs font-bold uppercase tracking-wider text-zinc-500">Assumptions</Label>
              <textarea id="assumptions" value={assumptions} onChange={(e) => setAssumptions(e.target.value)} disabled={!canDirectEdit || isReadOnly} rows={2}
                className="mt-2 w-full bg-zinc-50 border border-zinc-200 focus:border-blue-600 rounded-none p-3 text-base" data-testid="assumptions-input" />
            </div>
          </div>
        </div>

        {/* Margin Health Meter — advisory, never blocks */}
        {!financialAccess.is_financially_restricted && <MarginHealthMeter data={marginHealth} onExport={handleExportRiskSummary} />}

        {/* Line Items */}
        <div className="bg-white border border-zinc-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-800">Line Items</h2>
            {canDirectEdit && !isReadOnly && (
              <div className="flex gap-2 flex-wrap">
                <Button onClick={handleDownloadTemplate} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 text-xs" data-testid="download-template-button"><DownloadSimple size={16} className="mr-1" /> Template</Button>
                <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 text-xs" data-testid="bulk-import-button"><UploadSimple size={16} className="mr-1" /> Import Excel</Button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkImport} />
                <Button onClick={() => smartImportRef.current?.click()} variant="outline" className="border-blue-300 text-blue-700 rounded-none hover:bg-blue-50 text-xs" data-testid="smart-import-button"><Database size={16} className="mr-1" /> {importUploading ? 'Matching...' : 'Smart Import'}</Button>
                <input ref={smartImportRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleSmartImport} />
                <Button onClick={openMasterPicker} variant="outline" className="border-green-300 text-green-700 rounded-none hover:bg-green-50 text-xs" data-testid="from-master-button"><Database size={16} className="mr-1" /> From Master</Button>
                <Button onClick={addLineItem} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 text-xs" data-testid="add-line-item-button"><Plus size={16} className="mr-1" /> New Item</Button>
              </div>
            )}
          </div>

          {lineItems.length === 0 ? (
            <div className="text-center py-12 text-zinc-500" data-testid="empty-line-items">No line items yet.</div>
          ) : (
            <div className="space-y-4">
              {lineItems.map((item, index) => {
                const costs = computeLocalCosts(item);
                const trade = getTradeById(item.trade_id);
                const showSubRate = isSubcontractTrade(item.trade_id);
                const canEditField = canDirectEdit && !isReadOnly;

                return (
                  <div key={index} className="border border-zinc-200 p-4 bg-zinc-50" data-testid={`line-item-${index}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-zinc-500">#{index + 1} {trade ? `- ${trade.name}` : ''}</span>
                        {!financialAccess.is_financially_restricted && <MarginBadge row={marginHealth?.line_items?.[index]} />}
                        {!financialAccess.is_financially_restricted && canEditField &&
                          (marginHealth?.line_items?.[index]?.health === 'unknown' || (parseFloat(item.target_cost_per_unit) || 0) > 0) && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500" data-testid={`target-cost-wrap-${index}`}>
                            <span className="uppercase tracking-wide">Target ₹/unit</span>
                            <input type="number" value={item.target_cost_per_unit || ''}
                              onChange={(e) => updateLineItem(index, 'target_cost_per_unit', parseFloat(e.target.value) || 0)}
                              placeholder="set"
                              className="w-20 border border-zinc-200 rounded px-1.5 py-0.5 text-[11px] bg-white"
                              data-testid={`target-cost-${index}`} />
                          </span>
                        )}
                        {item.master_item_id && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200">From Master</span>}
                        {!item.master_item_id && item.description && canEditField && (
                          <button
                            type="button"
                            onClick={() => openSuggestDialog(item)}
                            className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 border border-amber-200 hover:bg-amber-100"
                            data-testid={`suggest-to-master-${index}`}
                            title="Suggest this item to be added to the Master Studio catalog"
                          >Suggest to Master</button>
                        )}
                      </div>
                      {canEditField && (
                        <div className="flex gap-1">
                          <Button onClick={() => removeLineItem(index)} variant="outline" size="sm" className="border-red-300 text-red-700 rounded-none hover:bg-red-50 h-7 px-2" data-testid={`remove-line-item-${index}`}><Trash size={14} /></Button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      {/* Trade */}
                      <div className="col-span-2 md:col-span-1">
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Trade *</Label>
                        {canEditField ? (
                          <select value={item.trade_id} onChange={(e) => updateLineItem(index, 'trade_id', e.target.value)}
                            className="mt-1 w-full bg-white border border-zinc-200 rounded-none p-2 text-xs" data-testid={`trade-select-${index}`}>
                            {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        ) : (
                          <div className="mt-1 flex items-center gap-1">
                            <p className="text-xs text-zinc-800 bg-white border border-zinc-200 p-2 flex-1">{trade?.name || '-'}</p>
                            {!isViewer && !quoteLocked && <button onClick={() => handleFieldEdit(index, 'trade_id', 'Trade', trade?.name || '')} className="p-1 text-amber-600 hover:bg-amber-50 border border-amber-200" data-testid={`edit-trade-${index}`}><PencilSimple size={12} /></button>}
                          </div>
                        )}
                      </div>
                      {/* Description */}
                      <div className="col-span-2 md:col-span-3">
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description *</Label>
                        {canEditField ? (
                          <LineItemTypeahead
                            value={item.description}
                            linkedItem={item.master_item_id ? { id: item.master_item_id, item_code: item.item_code || '' } : null}
                            onUnlink={() => {
                              // Preserve description so user can edit it; drop master linkage.
                              setLineItems(prev => {
                                const updated = [...prev];
                                updated[index] = {
                                  ...updated[index],
                                  master_item_id: null,
                                  item_code: '',
                                  source_type: '',
                                  material_recipe: null,
                                  manpower_recipe: null,
                                  // Reset master-derived Phase 1 fields too
                                  basic_rate_breakup: '',
                                  inclusions: [],
                                  exclusions: [],
                                  preferred_subcontractor_id: '',
                                };
                                return updated;
                              });
                            }}
                            onChange={(v) => {
                              setLineItems(prev => {
                                const updated = [...prev];
                                const cur = updated[index] || {};
                                // If row had a master link and user edited the description,
                                // break the link — user is authoring a different item.
                                const shouldUnlink = !!cur.master_item_id && v !== cur.description;
                                updated[index] = {
                                  ...cur,
                                  description: v,
                                  ...(shouldUnlink ? {
                                    master_item_id: null,
                                    item_code: '',
                                    source_type: '',
                                    material_recipe: null,
                                    manpower_recipe: null,
                                  } : {}),
                                };
                                return updated;
                              });
                            }}
                            onSelect={(selected) => {
                              // Only auto-populate from master items (not loose quote-history entries)
                              const isMaster = selected.source_type === 'master' && selected.master_item_id;
                              const materialRecipe = isMaster
                                ? (selected.materials || []).map(m => ({
                                    material_name: m.material_name || '',
                                    material_id: m.material_id || '',
                                    consumption_per_unit: m.quantity_per_unit || 0,
                                    unit: m.unit || '',
                                    wastage_percentage: m.wastage_percentage || 0,
                                    remarks: m.remarks || '',
                                  }))
                                : null;
                              const manpowerRecipe = isMaster
                                ? (selected.manpower_entries || []).map(mp => ({
                                    labour_type: mp.labour_type || 'Skilled',
                                    role_name: mp.role_name || '',
                                    manpower_per_unit: mp.manpower_per_unit || 0,
                                    remarks: mp.remarks || '',
                                  }))
                                : null;
                              const masterTrade = isMaster && selected.trade_id
                                ? trades.find(t => t.id === selected.trade_id)
                                : null;
                              setLineItems(prev => {
                                const updated = [...prev];
                                const current = updated[index] || {};
                                updated[index] = {
                                  ...current,
                                  description: selected.description || current.description,
                                  ...(selected.unit ? { unit: selected.unit } : {}),
                                  ...(selected.specification ? { specification: selected.specification } : {}),
                                  ...(selected.make ? { make: selected.make } : {}),
                                  ...(selected.hsn_code ? { hsn_code: selected.hsn_code } : {}),
                                  ...(isMaster ? {
                                    trade_id: selected.trade_id || current.trade_id,
                                    rate: selected.last_used_rate || selected.base_rate || current.rate || 0,
                                    master_item_id: selected.master_item_id,
                                    item_code: selected.item_code || '',
                                    source_type: 'master',
                                    material_recipe: materialRecipe && materialRecipe.length > 0 ? materialRecipe : (current.material_recipe || null),
                                    manpower_recipe: manpowerRecipe && manpowerRecipe.length > 0 ? manpowerRecipe : (current.manpower_recipe || null),
                                    // Phase 1 — Execution & client-facing
                                    execution_type: selected.execution_type || current.execution_type || 'in_house',
                                    basic_rate_breakup: selected.basic_rate_breakup || current.basic_rate_breakup || '',
                                    inclusions: selected.inclusions && selected.inclusions.length ? selected.inclusions : (current.inclusions || []),
                                    exclusions: selected.exclusions && selected.exclusions.length ? selected.exclusions : (current.exclusions || []),
                                    preferred_subcontractor_id: selected.preferred_subcontractor_id || current.preferred_subcontractor_id || '',
                                    ...(masterTrade?.default_hsn_code && !current.hsn_code && !selected.hsn_code
                                      ? { hsn_code: masterTrade.default_hsn_code } : {}),
                                  } : {
                                    // Non-master selection (e.g. history) — ensure master linkage is cleared
                                    master_item_id: null,
                                    item_code: '',
                                    source_type: selected.source_type || '',
                                    material_recipe: null,
                                    manpower_recipe: null,
                                  }),
                                };
                                return updated;
                              });
                              // Fire-and-forget usage bump for master items
                              if (isMaster && selected.master_item_id) {
                                try {
                                  axios.post(
                                    `${BACKEND_URL}/api/line-item-master/increment-usage/${selected.master_item_id}`,
                                    { rate: selected.base_rate || 0 },
                                    { withCredentials: true }
                                  ).catch(() => {});
                                } catch (_) { /* best-effort */ }
                              }
                            }}
                            className="mt-1"
                          />
                        ) : (
                          <div className="mt-1 flex items-center gap-1">
                            <p className="text-xs text-zinc-800 bg-white border border-zinc-200 p-2 flex-1 truncate">{item.description || '-'}</p>
                            {!isViewer && !quoteLocked && <button onClick={() => handleFieldEdit(index, 'description', 'Description', item.description)} className="p-1 text-amber-600 hover:bg-amber-50 border border-amber-200" data-testid={`edit-desc-${index}`}><PencilSimple size={12} /></button>}
                          </div>
                        )}
                      </div>
                      {/* Unit */}
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Unit *</Label>
                        {canEditField ? (
                          <Input value={item.unit} onChange={(e) => updateLineItem(index, 'unit', e.target.value)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`unit-${index}`} />
                        ) : (
                          <p className="mt-1 text-xs text-zinc-800 bg-white border border-zinc-200 p-2">{item.unit || '-'}</p>
                        )}
                      </div>
                      {/* Quantity */}
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Qty *</Label>
                        {canEditField ? (
                          <Input type="number" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`quantity-${index}`} />
                        ) : (
                          <div className="mt-1 flex items-center gap-1">
                            <p className="text-xs text-zinc-800 bg-white border border-zinc-200 p-2 flex-1">{item.quantity}</p>
                            {!isViewer && !quoteLocked && <button onClick={() => handleFieldEdit(index, 'quantity', 'Quantity', item.quantity)} className="p-1 text-amber-600 hover:bg-amber-50 border border-amber-200" data-testid={`edit-qty-${index}`}><PencilSimple size={12} /></button>}
                          </div>
                        )}
                      </div>
                      {/* Specification */}
                      <div className="col-span-2 md:col-span-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Specification</Label>
                        {canEditField ? (
                          <Input value={item.specification} onChange={(e) => updateLineItem(index, 'specification', e.target.value)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`specification-${index}`} />
                        ) : (
                          <p className="mt-1 text-xs text-zinc-800 bg-white border border-zinc-200 p-2 truncate">{item.specification || '-'}</p>
                        )}
                      </div>
                      {/* Make */}
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Make</Label>
                        {canEditField ? (
                          <Input value={item.make} onChange={(e) => updateLineItem(index, 'make', e.target.value)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`make-${index}`} />
                        ) : (
                          <p className="mt-1 text-xs text-zinc-800 bg-white border border-zinc-200 p-2">{item.make || '-'}</p>
                        )}
                      </div>
                      {/* Rate — FINANCIAL RESTRICTED */}
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Rate *</Label>
                        {canSeeFinancials ? (
                          canEditField ? (
                            <Input type="number" value={item.rate} onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs font-semibold" data-testid={`rate-${index}`} />
                          ) : (
                            <div className="mt-1 flex items-center gap-1">
                              <p className="text-xs font-semibold text-zinc-800 bg-white border border-zinc-200 p-2 flex-1">{item.rate}</p>
                              {!isViewer && !quoteLocked && <button onClick={() => handleFieldEdit(index, 'rate', 'Rate', item.rate)} className="p-1 text-amber-600 hover:bg-amber-50 border border-amber-200" data-testid={`edit-rate-${index}`}><PencilSimple size={12} /></button>}
                            </div>
                          )
                        ) : (
                          <p className="mt-1 text-xs text-zinc-400 bg-zinc-100 border border-zinc-200 p-2 font-mono" data-testid={`rate-restricted-${index}`}>{RESTRICTED}</p>
                        )}
                      </div>
                      {/* Subcontractor Rate — FINANCIAL RESTRICTED */}
                      {showSubRate && (
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Sub. Rate</Label>
                          {canSeeFinancials ? (
                            canEditField ? (
                              <Input type="number" value={item.subcontractor_rate} onChange={(e) => updateLineItem(index, 'subcontractor_rate', parseFloat(e.target.value) || 0)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`sub-rate-${index}`} />
                            ) : (
                              <div className="mt-1 flex items-center gap-1">
                                <p className="text-xs text-zinc-800 bg-white border border-zinc-200 p-2 flex-1">{item.subcontractor_rate}</p>
                                {!isViewer && !quoteLocked && <button onClick={() => handleFieldEdit(index, 'subcontractor_rate', 'Subcontractor Rate', item.subcontractor_rate)} className="p-1 text-amber-600 hover:bg-amber-50 border border-amber-200"><PencilSimple size={12} /></button>}
                              </div>
                            )
                          ) : (
                            <p className="mt-1 text-xs text-zinc-400 bg-zinc-100 border border-zinc-200 p-2 font-mono">{RESTRICTED}</p>
                          )}
                        </div>
                      )}
                      {/* Remarks */}
                      <div className={showSubRate ? '' : 'col-span-2 md:col-span-1'}>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Remarks</Label>
                        {canEditField ? (
                          <Input value={item.remarks} onChange={(e) => updateLineItem(index, 'remarks', e.target.value)} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs" data-testid={`remarks-${index}`} />
                        ) : (
                          <p className="mt-1 text-xs text-zinc-800 bg-white border border-zinc-200 p-2">{item.remarks || '-'}</p>
                        )}
                      </div>
                      {/* HSN Code */}
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">HSN</Label>
                        {canDirectEdit ? (
                          <Input value={item.hsn_code || ''} onChange={(e) => updateLineItem(index, 'hsn_code', e.target.value)} placeholder={trade?.default_hsn_code || ''} className="mt-1 bg-white border-zinc-200 rounded-none p-2 text-xs font-mono" data-testid={`hsn-${index}`} />
                        ) : (
                          <p className="mt-1 text-xs font-mono text-zinc-800 bg-white border border-zinc-200 p-2" data-testid={`hsn-display-${index}`}>{item.hsn_code || trade?.default_hsn_code || '-'}</p>
                        )}
                      </div>
                    </div>

                    {/* Reference Image — Optional */}
                    {canDirectEdit && (
                      <div className="mt-2 flex items-center gap-3">
                        <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 shrink-0">Ref. Image</Label>
                        {item.reference_image ? (
                          <div className="flex items-center gap-2">
                            <img src={`${BACKEND_URL}${item.reference_image}`} alt="ref" className="w-12 h-12 object-cover border border-zinc-200" />
                            <button onClick={() => updateLineItem(index, 'reference_image', '')} className="text-red-500 text-xs hover:underline">Remove</button>
                          </div>
                        ) : (
                          <label className="cursor-pointer text-xs text-blue-700 hover:underline" data-testid={`upload-img-${index}`}>
                            Upload image
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const fd = new FormData(); fd.append('image', file);
                              try {
                                const res = await axios.post(`${BACKEND_URL}/api/uploads/boq-images`, fd, { withCredentials: true });
                                updateLineItem(index, 'reference_image', res.data.url);
                                toast.success('Image uploaded');
                              } catch { toast.error('Upload failed'); }
                            }} />
                          </label>
                        )}
                      </div>
                    )}
                    {!canDirectEdit && item.reference_image && (
                      <div className="mt-2 flex items-center gap-2">
                        <Label className="text-xs text-zinc-500">Ref. Image:</Label>
                        <img src={`${BACKEND_URL}${item.reference_image}`} alt="ref" className="w-12 h-12 object-cover border border-zinc-200" />
                      </div>
                    )}

                    {/* Phase 1: Execution + Inclusions/Exclusions + Internal breakup */}
                    <div className="mt-3 border-t border-dashed border-zinc-200 pt-3 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Execution</Label>
                        {canDirectEdit ? (
                          <select
                            value={item.execution_type || ''}
                            onChange={(e) => updateLineItem(index, 'execution_type', e.target.value)}
                            className="text-xs bg-white border border-zinc-300 rounded-none px-2 py-1"
                            data-testid={`exec-type-${index}`}
                          >
                            <option value="">— Default (use Trade) —</option>
                            <option value="in_house">In-House</option>
                            <option value="sc_with_material">SC with Material</option>
                            <option value="sc_without_material">SC without Material</option>
                          </select>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-200">
                            {item.execution_type === 'sc_with_material' ? 'SC with Material'
                              : item.execution_type === 'sc_without_material' ? 'SC without Material'
                              : item.execution_type === 'in_house' ? 'In-House'
                              : '— Trade default —'}
                          </span>
                        )}
                        {(item.execution_type === 'sc_with_material' || item.execution_type === 'sc_without_material') && (
                          <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">
                            Vendor + Work Order required
                          </span>
                        )}
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-zinc-500 hover:text-zinc-800 select-none">
                          Inclusions / Exclusions / Internal Rate Breakup
                          {((item.inclusions || []).length + (item.exclusions || []).length > 0 || item.basic_rate_breakup) ? (
                            <span className="ml-2 text-[10px] bg-blue-50 text-blue-700 border border-blue-200 px-1.5">
                              {(item.inclusions || []).length} inc · {(item.exclusions || []).length} exc {item.basic_rate_breakup ? '· breakup' : ''}
                            </span>
                          ) : null}
                        </summary>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[10px] font-bold text-zinc-500 uppercase">Inclusions (one per line)</Label>
                            <textarea
                              value={(item.inclusions || []).join('\n')}
                              onChange={(e) => updateLineItem(index, 'inclusions', e.target.value.split('\n'))}
                              rows={3}
                              disabled={!canDirectEdit}
                              className="mt-1 w-full border border-zinc-200 px-2 py-1 text-xs font-mono"
                              data-testid={`inclusions-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold text-zinc-500 uppercase">Exclusions (one per line)</Label>
                            <textarea
                              value={(item.exclusions || []).join('\n')}
                              onChange={(e) => updateLineItem(index, 'exclusions', e.target.value.split('\n'))}
                              rows={3}
                              disabled={!canDirectEdit}
                              className="mt-1 w-full border border-zinc-200 px-2 py-1 text-xs font-mono"
                              data-testid={`exclusions-${index}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] font-bold text-zinc-500 uppercase flex items-center gap-1">
                              Basic Rate Breakup <span className="text-[9px] bg-zinc-100 text-zinc-500 px-1">INTERNAL</span>
                            </Label>
                            <textarea
                              value={item.basic_rate_breakup || ''}
                              onChange={(e) => updateLineItem(index, 'basic_rate_breakup', e.target.value)}
                              rows={3}
                              disabled={!canDirectEdit}
                              className="mt-1 w-full border border-zinc-200 px-2 py-1 text-xs font-mono"
                              placeholder={'Tile @ Rs 85/Sft\nAdhesive @ Rs 12/Sft'}
                              data-testid={`breakup-${index}`}
                            />
                          </div>
                        </div>
                      </details>
                    </div>

                    {/* Phase 2: Manpower Wastage Warning (in_house items only) */}
                    {(() => {
                      const exec = item.execution_type || '';
                      if (exec === 'sc_with_material' || exec === 'sc_without_material') return null;
                      const qty = parseFloat(item.quantity) || 0;
                      if (qty <= 0) return null;
                      const recipe = item.manpower_recipe || [];
                      const wastages = [];
                      for (const mp of recipe) {
                        const mpu = parseFloat(mp.manpower_per_unit) || 0;
                        const opd = parseFloat(mp.output_per_day) || (mpu > 0 ? 1 / mpu : 0);
                        if (mpu <= 0) continue;
                        const raw = qty * mpu;
                        const billable = Math.ceil(raw);
                        const wasted = +(billable - raw).toFixed(4);
                        const frac = billable > 0 ? wasted / billable : 0;
                        if (frac >= 0.20) {
                          wastages.push({
                            role: mp.role_name || mp.labour_type || 'Worker',
                            wasted: wasted.toFixed(2),
                            billable,
                            opd: opd ? opd.toFixed(0) : '?',
                          });
                        }
                      }
                      if (wastages.length === 0) return null;
                      return (
                        <div className="mt-2 border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid={`wastage-warning-${index}`}>
                          <div className="font-bold uppercase tracking-wide text-[10px] mb-1 flex items-center gap-1">
                            <Warning size={12} /> Manpower Wastage
                          </div>
                          {wastages.map((w, wi) => (
                            <div key={wi}>
                              <strong>{w.role}</strong> wastes <strong>{w.wasted}</strong> of {w.billable} day(s). 1 worker can do <strong>{w.opd} {item.unit}</strong>/day — qty too small for full utilisation.
                            </div>
                          ))}
                          <div className="text-[10px] mt-1 italic text-amber-700">
                            Consider grouping with similar items, or accepting the wastage and adjusting the rate.
                          </div>
                        </div>
                      );
                    })()}

                    {/* Cost Breakdown — FINANCIAL RESTRICTED */}
                    {canSeeFinancials ? (
                      <div className="mt-3 bg-white border border-zinc-200 p-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          <div><span className="text-zinc-500">Material Cost</span><p className="font-medium text-zinc-900 mt-0.5" data-testid={`material-cost-${index}`}>{fmtCurrency(costs.material_cost)}</p></div>
                          <div><span className="text-zinc-500">Labour Cost</span><p className="font-medium text-zinc-900 mt-0.5" data-testid={`labour-cost-${index}`}>{fmtCurrency(costs.labour_cost)}</p></div>
                          <div><span className="text-zinc-500">Margin</span><p className="font-medium text-zinc-900 mt-0.5" data-testid={`margin-cost-${index}`}>{fmtCurrency(costs.margin_cost)}</p></div>
                          <div><span className="text-zinc-500">Total Amount</span><p className="font-bold text-blue-700 mt-0.5" data-testid={`total-amount-${index}`}>{fmtCurrency(costs.total_amount)}</p></div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 bg-zinc-100 border border-zinc-200 p-3 flex items-center gap-2 text-xs text-zinc-400" data-testid={`cost-restricted-${index}`}>
                        <EyeSlash size={14} /> Financial breakdown hidden
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Variation Orders (locked quotes only) */}
        {quoteLocked && !isNew && (
          <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="vo-section">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold tracking-tight text-zinc-800">Variation Orders</h2>
              <Button onClick={() => navigate(`/projects/${projectId}/variation-orders`)} variant="outline" className="border-amber-300 text-amber-700 rounded-none hover:bg-amber-50 text-xs" data-testid="manage-vo-button">
                Manage VOs →
              </Button>
            </div>
            {variationOrders.length === 0 ? (
              <p className="text-sm text-zinc-500">No variation orders yet. <button onClick={() => navigate(`/projects/${projectId}/variation-orders`)} className="text-blue-700 underline">Create one</button></p>
            ) : (
              <div className="space-y-2">
                {variationOrders.map((vo) => (
                  <div key={vo.id} className="border border-zinc-200 bg-zinc-50 p-3 flex justify-between items-start" data-testid={`vo-item-${vo.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-zinc-800">{vo.vo_number || `VO#${vo.id?.slice(-4)}`}: {vo.title || vo.description}</p>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 ${
                          vo.status === 'Approved' ? 'bg-green-50 text-green-700 border border-green-200' :
                          vo.status === 'Rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-zinc-100 text-zinc-600 border border-zinc-200'
                        }`}>{vo.status || 'Draft'}</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">{(vo.line_items || []).length} line items · {vo.reason}</p>
                      {canSeeFinancials && vo.total_amount != null && (
                        <span className="text-xs font-bold text-blue-700 mt-0.5 inline-block">Total: {fmtCurrency(vo.total_amount)}</span>
                      )}
                    </div>
                  </div>
                ))}
                {canSeeFinancials && (
                  <div className="text-right mt-2 text-sm font-bold text-amber-700" data-testid="vo-total">
                    VO Total: {fmtCurrency(variationOrders.reduce((s, vo) => s + (vo.total_amount || 0), 0))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary — FINANCIAL RESTRICTED */}
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-800 mb-4">Summary</h2>
          {canSeeFinancials ? (
            <div className="space-y-3 max-w-md ml-auto">
              <div className="flex justify-between text-base"><span className="text-zinc-700">Subtotal:</span><span className="font-medium text-zinc-900" data-testid="subtotal-amount">{fmtCurrency(subtotal)}</span></div>
              <div className="flex justify-between text-base"><span className="text-zinc-700">{taxType === 'IGST' ? 'IGST @ 18%' : 'CGST & SGST @ 9% each'}:</span><span className="font-medium text-zinc-900" data-testid="tax-amount">{fmtCurrency(taxAmount)}</span></div>
              <div className="border-t border-zinc-200 pt-3 flex justify-between text-lg"><span className="font-bold text-zinc-900">Total:</span><span className="font-bold text-blue-700" data-testid="total-amount">{fmtCurrency(total)}</span></div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-zinc-400 py-4" data-testid="summary-restricted">
              <EyeSlash size={20} />
              <p className="text-sm">Financial summary is restricted for your role. Request access to view totals.</p>
            </div>
          )}
        </div>
      </main>

      {/* Master Picker Dialog */}
      <Dialog open={masterDialogOpen} onOpenChange={setMasterDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Select from Line Item Master</DialogTitle></DialogHeader>
          <div className="flex gap-2 mt-3 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={masterSearch} onChange={(e) => setMasterSearch(e.target.value)} placeholder="Search items..." className="rounded-none pl-10" data-testid="master-picker-search" />
            </div>
            <select value={masterFilterTrade} onChange={(e) => setMasterFilterTrade(e.target.value)} className="bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm">
              <option value="">All Trades</option>
              {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {filteredMasterItems.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">No items found.</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredMasterItems.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 border border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => { addFromMaster(item); setMasterDialogOpen(false); }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2"><span className="text-xs font-mono bg-zinc-100 text-zinc-600 px-1.5 py-0.5">{item.item_code}</span><span className="text-sm font-semibold text-zinc-800 truncate">{item.description}</span>{item.make && <span className="text-xs text-zinc-500">({item.make})</span>}</div>
                    <div className="flex gap-3 mt-1 text-xs text-zinc-500">
                      <span>{item.trade_name}</span><span>{item.unit}</span>
                      {canSeeFinancials && <span>{fmtCurrency(item.base_rate)}</span>}
                      {item.specification && <span className="truncate max-w-[200px]">{item.specification}</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-none border-green-300 text-green-700 h-7 text-xs ml-3"><Plus size={14} className="mr-1" /> Add</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Smart Import Dialog */}
      <Dialog open={smartImportOpen} onOpenChange={setSmartImportOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">Smart Import — Match Results</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 mt-1 mb-4">{importResults.filter(r => r.status === 'matched').length} matched, {importResults.filter(r => r.status === 'new_item').length} new</p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {importResults.map((r, idx) => (
              <div key={idx} className={`p-3 border ${r.status === 'matched' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {r.status === 'matched' ? <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 border border-green-200">Matched ({(r.confidence * 100).toFixed(0)}%)</span> : <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 border border-amber-200">New Item</span>}
                      <span className="text-sm font-medium text-zinc-800">{r.input.description}</span>
                    </div>
                    {r.match && <p className="text-xs text-green-700 mt-1">Master: {r.match.item_code} — {r.match.description}</p>}
                  </div>
                  {r.status === 'matched' && (
                    <div className="flex items-center gap-2 ml-4">
                      <Button onClick={() => { const u = [...importResults]; u[idx].action = 'accept'; setImportResults(u); }} variant={r.action === 'accept' ? 'default' : 'outline'} size="sm" className={`rounded-none h-7 text-xs ${r.action === 'accept' ? 'bg-green-700 text-white' : 'border-green-300 text-green-700'}`}><Check size={14} className="mr-1" /> Accept</Button>
                      <Button onClick={() => { const u = [...importResults]; u[idx].action = 'override'; setImportResults(u); }} variant={r.action === 'override' ? 'default' : 'outline'} size="sm" className={`rounded-none h-7 text-xs ${r.action === 'override' ? 'bg-amber-600 text-white' : 'border-amber-300 text-amber-700'}`}><X size={14} className="mr-1" /> Override</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-zinc-200">
            <Button onClick={() => setSmartImportOpen(false)} variant="outline" className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={applySmartImport} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="apply-smart-import">Apply {importResults.length} Items</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BOQ Change Request Dialog */}
      <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold flex items-center gap-2"><ShieldWarning size={18} className="text-amber-700" /> Propose BOQ Change</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 mt-1 mb-4">Direct editing is controlled. Submit a change request for approval.</p>
          {changeTarget && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Field</Label>
                <p className="text-sm text-zinc-800 mt-1">{changeTarget.label}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Current Value</Label>
                <p className="text-sm text-zinc-800 mt-1 bg-red-50 px-2 py-1 border border-red-200" data-testid="change-old-value">{changeTarget.oldValue || '-'}</p>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">New Value *</Label>
                <Input value={changeNewValue} onChange={(e) => setChangeNewValue(e.target.value)} className="mt-1 rounded-none" placeholder="Enter proposed value" data-testid="change-new-value" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Reason *</Label>
                <textarea value={changeReason} onChange={(e) => setChangeReason(e.target.value)} rows={2} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" placeholder="Why is this change needed?" data-testid="change-reason" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                <Button onClick={() => setChangeDialogOpen(false)} variant="outline" className="rounded-none border-zinc-300 text-xs">Cancel</Button>
                <Button onClick={submitChangeRequest} disabled={changeSubmitting} className="bg-amber-600 hover:bg-amber-700 text-white rounded-none text-xs" data-testid="submit-change-btn">
                  {changeSubmitting ? 'Submitting...' : 'Submit Change Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Financial Access Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-bold flex items-center gap-2"><LockOpen size={18} className="text-blue-700" /> Request Financial Access</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 mt-1 mb-4">Access will be time-bound and requires Admin then Super Admin approval.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Project</Label>
              <p className="text-sm text-zinc-800 mt-1">{project?.pi_number} — {project?.client_name}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Duration (days) *</Label>
              <select value={requestDuration} onChange={(e) => setRequestDuration(parseInt(e.target.value))} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" data-testid="request-duration">
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Reason *</Label>
              <textarea value={requestReason} onChange={(e) => setRequestReason(e.target.value)} rows={3} className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-sm" placeholder="Explain why you need financial access" data-testid="request-reason" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
              <Button onClick={() => setRequestDialogOpen(false)} variant="outline" className="rounded-none border-zinc-300 text-xs">Cancel</Button>
              <Button onClick={handleRequestFinancialAccess} disabled={requestSubmitting} className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="submit-access-request-btn">
                {requestSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggest Item to Master Dialog */}
      <Dialog open={suggestDialogOpen} onOpenChange={setSuggestDialogOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Database size={18} className="text-amber-700" /> Suggest Item to Master
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500 mt-1 mb-4">
            This item isn't in the Line Item Master yet. Submit it for Admin review — once recipes are added via Master Studio, it'll be published and available everywhere.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Description *</Label>
              <Input
                value={suggestForm.description}
                onChange={(e) => setSuggestForm({ ...suggestForm, description: e.target.value })}
                className="mt-1 border-zinc-200 rounded-none text-xs"
                data-testid="suggest-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Unit *</Label>
                <Input
                  value={suggestForm.unit}
                  onChange={(e) => setSuggestForm({ ...suggestForm, unit: e.target.value })}
                  className="mt-1 border-zinc-200 rounded-none text-xs"
                  data-testid="suggest-unit"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-zinc-500">Trade</Label>
                <select
                  value={suggestForm.trade_id}
                  onChange={(e) => setSuggestForm({ ...suggestForm, trade_id: e.target.value })}
                  className="mt-1 w-full border border-zinc-200 rounded-none p-1.5 text-xs"
                  data-testid="suggest-trade"
                >
                  <option value="">-- Select trade --</option>
                  {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Urgency</Label>
              <select
                value={suggestForm.urgency}
                onChange={(e) => setSuggestForm({ ...suggestForm, urgency: e.target.value })}
                className="mt-1 w-full border border-zinc-200 rounded-none p-1.5 text-xs"
                data-testid="suggest-urgency"
              >
                <option value="low">Low — can wait</option>
                <option value="medium">Medium — needed this week</option>
                <option value="high">High — blocking a quote</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-zinc-500">Remarks / Specification</Label>
              <textarea
                value={suggestForm.remarks}
                onChange={(e) => setSuggestForm({ ...suggestForm, remarks: e.target.value })}
                rows={3}
                className="mt-1 w-full border border-zinc-200 rounded-none p-2 text-xs"
                placeholder="Any spec notes, expected rate, reference make, or context"
                data-testid="suggest-remarks"
              />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button onClick={() => setSuggestDialogOpen(false)} variant="outline" className="rounded-none border-zinc-300 text-xs" data-testid="suggest-cancel">Cancel</Button>
              <Button onClick={submitSuggestion} disabled={suggestSubmitting} className="bg-amber-700 hover:bg-amber-800 text-white rounded-none text-xs" data-testid="suggest-submit">
                {suggestSubmitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
